import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { adminClient } from "@/lib/api";
import { isSetter, TEAM_NAME } from "@/lib/dvStats";
import { useT } from "@/lib/i18n";

const TEAM = "__team__";

export default function SeasonView({ endpoint = "/datavolley/season", client = adminClient }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [pid, setPid] = useState(TEAM);

  useEffect(() => {
    client
      .get(endpoint)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load season data"));
  }, [endpoint, client]);

  const player = data?.players.find((p) => p.player_id === pid);
  const setter = !!player && isSetter(player);
  const series = useMemo(() => {
    if (!data) return [];
    return data.matches
      .map((m, i) => {
        const s = pid === TEAM ? m.team : m.players[pid];
        if (!s) return null;
        return {
          idx: i + 1,
          label: `${m.date.slice(5)} ${m.opponent}`,
          opponent: m.opponent,
          date: m.date,
          rec: setter ? (s.set_total ? s.set_pct : null) : s.reception_total ? s.reception_pct : null,
          att: s.attack_total ? s.attack_pct : null,
          recAttempts: setter ? s.set_total : s.reception_total,
          attAttempts: s.attack_total,
          aces: s.aces,
          blocks: s.blocks,
        };
      })
      .filter(Boolean);
  }, [data, pid, setter]);

  const recLabel = setter ? t("dv_set_pct") : t("dv_rec_pct");
  const avg = (key, wKey) => {
    const num = series.reduce((a, s) => a + ((s[key] ?? 0) * s[wKey]) / 100, 0);
    const den = series.reduce((a, s) => a + s[wKey], 0);
    return den ? Math.round((num * 100) / den) : 0;
  };

  if (!data) return <p className="text-center text-slate-500 py-10">…</p>;
  if (data.matches.length === 0) {
    return <p className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="dv-season-empty">{t("dv_no_matches")}</p>;
  }

  return (
    <div className="flex flex-col gap-4" data-testid="dv-season-view">
      <div className="rounded-2xl bg-slate-900 px-4 py-3 text-white">
        <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{t("dv_season")}</p>
        <p className="font-heading font-black text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-sunset" /> {t("dv_progress")} · {data.matches.length} {t("dv_matches").toLowerCase()}
        </p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="dv-season-players">
        <Chip active={pid === TEAM} onClick={() => setPid(TEAM)} testid="dv-season-chip-team">
          <span className="font-heading font-black">{TEAM_NAME}</span>
        </Chip>
        {data.players.map((p) => (
          <Chip key={p.player_id} active={pid === p.player_id} onClick={() => setPid(p.player_id)} testid={`dv-season-chip-${p.number}`}>
            <span className="font-heading font-black">#{p.number}</span> <span className="text-sm font-bold">{p.name}</span>
            <span className="block text-[11px] opacity-70">{p.matches} {t("dv_matches").toLowerCase()}</span>
          </Chip>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label={`${recLabel} · ${t("dv_season_avg")}`} value={`${avg("rec", "recAttempts")}%`} cls={setter ? "bg-emerald-700" : "bg-emerald-500"} testid="dv-season-avg-rec" />
        <Card label={`${t("dv_att_pct")} · ${t("dv_season_avg")}`} value={`${avg("att", "attAttempts")}%`} cls="bg-rose-500" testid="dv-season-avg-att" />
        <Card label={t("dv_aces")} value={series.reduce((a, s) => a + s.aces, 0)} cls="bg-sky-600" testid="dv-season-aces" />
        <Card label={t("dv_blocks")} value={series.reduce((a, s) => a + s.blocks, 0)} cls="bg-amber-400 text-slate-900" testid="dv-season-blocks" />
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-soft p-4" data-testid="dv-season-chart">
        <p className="font-heading font-black text-slate-900 mb-3">
          {player ? `#${player.number} ${player.name}` : TEAM_NAME} — {recLabel} / {t("dv_att_pct")}
        </p>
        <div className="h-72 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 10, right: 20, left: -10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={series.length > 6 ? -25 : 0} height={series.length > 6 ? 60 : 30} textAnchor={series.length > 6 ? "end" : "middle"} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748b" }} unit="%" />
              <Tooltip
                formatter={(v, name, item) => [`${v}% (${name === recLabel ? item.payload.recAttempts : item.payload.attAttempts})`, name]}
                labelFormatter={(l, p) => (p?.[0] ? `${p[0].payload.date} · ${TEAM_NAME} vs ${p[0].payload.opponent}` : l)}
                contentStyle={{ borderRadius: 12, fontSize: 13 }}
              />
              <Legend />
              <Line connectNulls type="monotone" dataKey="rec" name={recLabel} stroke={setter ? "#047857" : "#10b981"} strokeWidth={3} dot={{ r: 5 }} activeDot={{ r: 7 }} />
              <Line connectNulls type="monotone" dataKey="att" name={t("dv_att_pct")} stroke="#f43f5e" strokeWidth={3} dot={{ r: 5 }} activeDot={{ r: 7 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-soft">
        <table className="w-full min-w-[640px] text-sm" data-testid="dv-season-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3">{t("dv_matches")}</th>
              <th className="px-3 py-3 text-emerald-800 bg-emerald-50" data-testid="dv-season-rec-th">{recLabel}</th>
              <th className="px-3 py-3 text-rose-800 bg-rose-50">{t("dv_att_pct")}</th>
              <th className="px-3 py-3 text-sky-800 bg-sky-50">{t("dv_aces")}</th>
              <th className="px-3 py-3 text-amber-800 bg-amber-50">{t("dv_blocks")}</th>
            </tr>
          </thead>
          <tbody>
            {series.map((s) => (
              <tr key={s.idx} className="border-b border-slate-100">
                <td className="px-4 py-2.5">
                  <p className="font-bold text-slate-900"><span className="text-slate-400 font-medium">{TEAM_NAME} vs</span> {s.opponent}</p>
                  <p className="text-[11px] text-slate-500">{s.date}</p>
                </td>
                <td className="text-center font-heading font-black text-emerald-700">{s.rec ?? "–"}{s.rec != null && "%"} <span className="text-xs font-body font-medium text-slate-400">({s.recAttempts})</span></td>
                <td className="text-center font-heading font-black text-rose-700">{s.att ?? "–"}{s.att != null && "%"} <span className="text-xs font-body font-medium text-slate-400">({s.attAttempts})</span></td>
                <td className="text-center font-heading font-black text-sky-700">{s.aces}</td>
                <td className="text-center font-heading font-black text-amber-700">{s.blocks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`dv-btn shrink-0 min-h-[56px] px-4 rounded-2xl border-2 text-left ${
        active ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function Card({ label, value, cls, testid }) {
  return (
    <div className={`rounded-2xl p-4 text-white ${cls}`} data-testid={testid}>
      <p className="text-xs font-bold uppercase tracking-widest opacity-90">{label}</p>
      <p className="font-heading font-black text-3xl mt-1 leading-none">{value}</p>
    </div>
  );
}
